
import { Vue, Component, Prop, Ref, Watch } from "vue-property-decorator";
type ImagePropType = string | Blob | File;
function getMouseEvenPosition(ev: MouseEvent) {
  return [ev.x, ev.y];
}

/**
 * 判断事件是否在元素elem上触发
 */
function isTriggerOnEl(elem: HTMLElement, ev: Event) {
  const _isTriggerOnEl = (el: HTMLElement): boolean => {
    if (el === elem) {
      return true;
    } else if (el.parentElement) {
      return _isTriggerOnEl(el.parentElement);
    } else {
      return false;
    }
  };
  if (ev.target instanceof HTMLElement) {
    return _isTriggerOnEl(ev.target);
  } else {
    return false;
  }
}
@Component
export default class ImageCrop extends Vue {
  @Prop()
  image!: ImagePropType;
  /** 是否固定比例 */
  @Prop({ required: false, default: false })
  ratioFixed?: boolean;
  /** 是否固定比例 */
  @Prop({ required: false, default: false })
  limitSelectingOnImage?: boolean;
  @Ref("preview") previewEl!: HTMLElement;
  @Ref("canvas") canvas!: HTMLCanvasElement;
  @Ref("img") imgEl!: HTMLImageElement;
  @Ref("selectArea") selectAreaEl!: HTMLImageElement;
  imagePreviewSrc = "";
  selecting = false;
  moving = false;
  startPoint: number[] = [];
  endPoint: number[] = [];
  moveStartPoint: number[] = [];
  selectAreaStyle: { [x: string]: string | number } = {};
  imgRect: DOMRect | null = null;
  isCanvasRender = false;
  documentMouseUpHandler?: (ev: MouseEvent) => void;
  documentMouseMoveHandler?: (ev: MouseEvent) => void;
  @Watch("image", { immediate: true })
  handleImageChange(val: ImagePropType) {
    if (val) {
      this.renderImage(val);
    }
  }
  get overlayVisible() {
    return Object.keys(this.selectAreaStyle).length > 0;
  }
  get footerVisible() {
    const isSelectedArea = Object.keys(this.selectAreaStyle).length > 0;
    return !(this.selecting || this.moving) && isSelectedArea;
  }
  mounted() {
    this.renderImage(
      "http://bw-picture-service.obs.cn-south-1.myhuaweicloud.com/mdPlan/8448998b46665117223c481f5b1cb90f.jpg"
    );
    this.documentMouseUpHandler = () => {
      this.moving = false;
      this.selecting = false;
    };
    document.addEventListener("mouseup", this.documentMouseUpHandler);
    this.documentMouseMoveHandler = (ev) => {
      const point = getMouseEvenPosition(ev);
      if (this.moving) {
        const moveEndPoint = getMouseEvenPosition(ev);
        const movingVal = [
          moveEndPoint[0] - this.moveStartPoint[0],
          moveEndPoint[1] - this.moveStartPoint[1],
        ];
        const startPoint = [
          this.startPoint[0] + movingVal[0],
          this.startPoint[1] + movingVal[1],
        ];
        const endPoint = [
          this.endPoint[0] + movingVal[0],
          this.endPoint[1] + movingVal[1],
        ];
        this.startPoint = startPoint;
        this.endPoint = endPoint;
        this.drawSelectedArea();
        this.moveStartPoint = moveEndPoint;
      } else if (this.selecting && this.isPointInImgRect(point)) {
        let endPoint = getMouseEvenPosition(ev);
        this.endPoint = endPoint;
        this.drawSelectedArea();
      }
    };
    document.addEventListener("mousemove", this.documentMouseMoveHandler);
  }
  handleImgLoad() {
    const imgRect = (this.imgEl as HTMLElement).getBoundingClientRect();
    this.imgRect = imgRect;
  }
  limitPointInWrapper(
    startPoint: number[],
    endPoint: number[],
    wrapper: HTMLElement
  ) {
    const wrapperRect = wrapper.getBoundingClientRect();
    const w = Math.abs(endPoint[0] - startPoint[0]);
    const h = Math.abs(endPoint[1] - startPoint[1]);
    const isOverXLeft =
      startPoint[0] < wrapperRect.left || endPoint[0] < wrapperRect.left;
    const isOverXRight =
      startPoint[0] > wrapperRect.right || endPoint[0] > wrapperRect.right;
    const isOverYTop =
      startPoint[1] < wrapperRect.top || endPoint[1] < wrapperRect.top;
    const isOverYBottom =
      startPoint[1] > wrapperRect.bottom || endPoint[1] > wrapperRect.bottom;
    if (isOverXLeft) {
      startPoint[0] = wrapperRect.left;
      endPoint[0] = startPoint[0] + w;
    } else if (isOverXRight) {
      endPoint[0] = wrapperRect.right;
      startPoint[0] = endPoint[0] - w;
    }
    if (isOverYTop) {
      startPoint[1] = wrapperRect.top;
      endPoint[1] = startPoint[1] + h;
    } else if (isOverYBottom) {
      endPoint[1] = wrapperRect.bottom;
      startPoint[1] = endPoint[1] - h;
    }
  }
  beforeDestroy() {
    if (this.documentMouseUpHandler) {
      document.removeEventListener("mouseup", this.documentMouseUpHandler);
    }
    if (this.documentMouseMoveHandler) {
      document.removeEventListener("mousemove", this.documentMouseMoveHandler);
    }
  }
  handlePreviewerMouseDown(ev: MouseEvent) {
    const selectWrapper = this.limitSelectingOnImage
      ? this.imgEl
      : this.previewEl;
    if (this.isTriggerInSelectArea(ev)) {
      this.moveStartPoint = getMouseEvenPosition(ev);
      this.moving = true;
    } else if (isTriggerOnEl(selectWrapper, ev)) {
      this.selecting = true;
      this.startPoint = getMouseEvenPosition(ev);
    }
  }
  isTriggerInSelectArea(ev: Event) {
    return this.isSelected() && isTriggerOnEl(this.selectAreaEl, ev);
  }
  isSelected() {
    return this.startPoint.length === 2 && this.endPoint.length === 2;
  }
  isPointInImgRect(point: number[]) {
    if (!this.imgRect) {
      return false;
    }
    const imgRect = this.imgRect as DOMRect;
    return (
      point[0] > imgRect.left &&
      point[0] < imgRect.right &&
      point[1] > imgRect.top &&
      point[1] < imgRect.bottom
    );
  }
  drawSelectedArea() {
    const limitWrapper = this.limitSelectingOnImage
      ? this.imgEl
      : this.previewEl;
    this.limitPointInWrapper(this.startPoint, this.endPoint, limitWrapper);
    const offsetX = this.endPoint[0] - this.startPoint[0];
    const offsetY = this.endPoint[1] - this.startPoint[1];
    let width = Math.abs(offsetX);
    let height = Math.abs(offsetY);
    if (this.ratioFixed) {
      const len = Math.max(width, height);
      width = len;
      height = len;
      this.endPoint = [
        this.startPoint[0] + len * (offsetX >= 0 ? 1 : -1),
        this.startPoint[1] + len * (offsetY >= 0 ? 1 : -1),
      ];
    }
    const wrapperRect = this.previewEl.getBoundingClientRect();
    const x = Math.min(this.startPoint[0], this.endPoint[0]);
    const y = Math.min(this.startPoint[1], this.endPoint[1]);
    const left = x - wrapperRect.left;
    const top = y - wrapperRect.top;
    const imgRect = this.imgEl.getBoundingClientRect();
    this.selectAreaStyle = {
      width: width + "px",
      height: height + "px",
      left: left + "px",
      top: top + "px",
      border: "1px dashed #f0f0f0",
      // boxSizing: "border-box",
      backgroundImage: `url(${this.imagePreviewSrc})`,
      backgroundRepeat: "no-repeat",
      "background-position": `${imgRect.left - x - 1}px ${
        imgRect.top - y - 1
      }px`,
    };
  }
  clearSelect() {
    this.selectAreaStyle = {};
    this.startPoint = [];
    this.endPoint = [];
  }
  crop() {
    //
  }
  renderImage(val: ImagePropType) {
    console.log("renderImage", val);

    if (typeof val === "string") {
      this.imagePreviewSrc = val;
    }
    // const canvas = this.canvas;
    // canvas.width = 800;
    // canvas.height = 800;
    // const ctx = canvas?.getContext("2d");
    // if (!ctx) {
    //   return;
    // }
    // if (typeof val === "string") {
    //   //
    //   var image = new Image();
    //   image.src = val;
    //   image.onload = function () {
    //     const w = image.width;
    //     const h = image.height;
    //     let renderW = 0;
    //     let renderH = 0;
    //     if (w >= h) {
    //       renderW = canvas.width;
    //       renderH = (h / w) * renderW;
    //     } else {
    //       renderH = canvas.height;
    //       renderW = (w / h) * renderH;
    //     }
    //     ctx.drawImage(image, 0, 0, renderW, renderH);
    //     let pattern = ctx.createPattern(image, "no-repeat");
    //     (ctx as any).fillStyle = pattern;
    //   };
    // }
  }
}
